/**
 * Frozen ActivityLogs filter snapshots for staff reports.
 * Each set is a copy of the logs from /logs at pin time —
 * later filter changes never mutate older sets.
 */

import {
  createInitialDescriptionBlocks,
  newReportBlockId,
  ReportDescriptionBlock,
} from './reportDescriptionBlocks';
import {
  parseActivityLogsTypeAttributeModeParam,
  type ActivityLogsTypeAttributeMode,
} from 'pages/Logs/logUtils';

/** How many separate ActivityLogs snapshots one report may hold. */
export const MAX_REPORT_ACTIVITY_SETS = 25;
/** Composer list — five set rows per page. */
export const REPORT_ACTIVITY_SETS_PER_PAGE = 5;

export type ReportActivitySetRow = {
  sourceId: string,
  tmpId: number,
  type: string,
  message: string,
  date?: string,
  /** Cash amount from activity_logs.value. */
  value?: string,
  /** Inventory units from activity_logs (shown when /logs header is Units). */
  units?: string,
  /** Actual log type / SKU when the Activity column is a rollup label. */
  activityLogType?: string,
};

export type ReportActivitySet = {
  id: string,
  /** 1-based serial shown in the form ("1", "2", …). */
  serial: number,
  rows: ReportActivitySetRow[],
  /** Description frozen with this set (shown above its charts). */
  descBlocks: ReportDescriptionBlock[],
  /** When false, preview/PDF/receiver skip metrics for this set only. */
  showCharts: boolean,
  /**
   * Frozen /logs value-column mode at pin time —
   * units → show units; otherwise cash value.
   */
  valueMode: 'value' | 'units',
  /** Optional dark set label in the chart header (empty → "ActivityLogs N"). */
  chartsTitle: string,
  /**
   * When true, reporting preview/PDF rolls rows up by type and shows the type
   * label instead of each log message (/logs table is unchanged).
   */
  combineLogTypes: boolean,
  /** Frozen /logs Activity-header attribute at pin time. */
  typeAttributeMode?: ActivityLogsTypeAttributeMode,
};

export const createReportActivitySet = (
  rows: ReportActivitySetRow[],
  serial: number,
  descBlocks?: ReportDescriptionBlock[],
  showCharts = true,
  valueMode: 'value' | 'units' = 'value',
  chartsTitle = '',
  combineLogTypes = false,
  typeAttributeMode?: ActivityLogsTypeAttributeMode,
): ReportActivitySet => ({
  id: newReportBlockId(),
  serial,
  // Keep every row from the ActivityLogs filter — no per-set log cap.
  rows: rows.map((r, i) => ({
    ...r,
    tmpId: i + 1,
  })),
  descBlocks: (descBlocks && descBlocks.length)
    ? descBlocks
    : createInitialDescriptionBlocks(),
  showCharts,
  valueMode: valueMode === 'units' ? 'units' : 'value',
  chartsTitle: String(chartsTitle || '').slice(0, 80),
  combineLogTypes: Boolean(combineLogTypes),
  typeAttributeMode: parseActivityLogsTypeAttributeModeParam(typeAttributeMode),
});

/** Keep serials 1..n after remove / reorder. */
export const renumberReportActivitySets = (sets: ReportActivitySet[]): ReportActivitySet[] => (
  sets.map((s, i) => ({
    ...s,
    serial: i + 1,
    descBlocks: Array.isArray(s.descBlocks) && s.descBlocks.length
      ? s.descBlocks
      : createInitialDescriptionBlocks(),
    // Preserve per-set chart toggle (default on for older snapshots).
    showCharts: s.showCharts !== false,
    valueMode: s.valueMode === 'units' ? 'units' : 'value',
    chartsTitle: typeof s.chartsTitle === 'string' ? s.chartsTitle.slice(0, 80) : '',
    combineLogTypes: s.combineLogTypes === true,
    typeAttributeMode: parseActivityLogsTypeAttributeModeParam(s.typeAttributeMode),
  }))
);
